'use strict';

const { getSelectedSupplier } = require('public/js/lib/supplier-state');
const { SUPPLIER_RESOLVED_EVENT } = require('public/js/lib/supplier-events');
const { waitForSupplierResolution } = require('public/js/lib/supplier-ready');

const LISTING_SELECTOR = '.search-listing-module';

/**
 * Supplier the server priced the current listing HTML for.
 *
 * Returns null when the attribute is absent, which means the page has no supplier-dependent pricing
 * to begin with (non-lead-gen store, or no listing on this page) and nothing needs refreshing.
 *
 * @returns {string|null}
 */
function getRenderedFulfillmentHash() {
	const $listing = rp$(LISTING_SELECTOR);
	if ($listing.length === 0) {
		return null;
	}

	const renderedHash = $listing.attr('data-ssr-fulfillment-hash');

	return typeof renderedHash === 'string' ? renderedHash : null;
}

/**
 * @returns {number} Page currently displayed, defaulting to the first.
 */
function getCurrentPage() {
	const page = Number(rp_app.query.all(rp_app.location.href).page);

	return Number.isInteger(page) && page > 0 ? page : 1;
}

/**
 * Re-fetches the listing for the supplier now selected.
 *
 * Reuses the pagination pipeline, which already re-renders `.search-listing-module` server-side with
 * session-aware pricing, preserves facets and filters, and rebinds add-to-cart. Refreshing is
 * skipped when the server already rendered for this supplier, so an expired supplier cache over a
 * live session (the TTL is well short of a browsing session) costs nothing.
 *
 * @param {string} supplierHash
 * @returns {boolean} True when a refresh was requested.
 */
function refreshListingForSupplier(supplierHash) {
	const renderedHash = getRenderedFulfillmentHash();
	if (renderedHash === null || renderedHash === supplierHash) {
		return false;
	}

	rp$(document).trigger('searchResults:pageChange', getCurrentPage());

	return true;
}

/**
 * Keeps listing pricing in step with the selected supplier without a full page reload.
 */
function bindSupplierListingRefresh() {
	let heardBroadcast = false;

	rp$(document).on(SUPPLIER_RESOLVED_EVENT, function handleSupplierResolved(_event, payload) {
		const supplierHash = payload?.supplier?.hash;
		if (typeof supplierHash !== 'string' || supplierHash === '') {
			return;
		}

		heardBroadcast = true;
		refreshListingForSupplier(supplierHash);
	});

	// Resolution can settle before this module binds, and the broadcast is not replayed, so
	// subscribing alone can leave the listing priced for whoever the server rendered it for.
	//
	// Skipped once a broadcast has been heard, because both fire on an ordinary load and the
	// rendered hash does not catch up until the re-fetch lands — so this would read the same stale
	// markup and ask for the listing a second time. Deliberately scoped to this catch-up path
	// rather than to the refresh itself: a broadcast must always be free to retry, or a re-fetch
	// that fails would leave the listing stale until a reload.
	waitForSupplierResolution().then(() => {
		const supplier = getSelectedSupplier();
		if (heardBroadcast || supplier === null) {
			return;
		}

		refreshListingForSupplier(supplier.hash);
	});
}

module.exports = {
	bindSupplierListingRefresh,
	refreshListingForSupplier,
};
